import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Calculator, 
  CheckCircle2, 
  ArrowRight, 
  Phone, 
  Mail, 
  Truck, 
  Clock, 
  ShieldAlert, 
  Layers, 
  UserCheck, 
  AlertTriangle 
} from 'lucide-react';
import { useMetadata } from '../hooks/useMetadata';

export default function Prices() {
  const [cmsPage, setCmsPage] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const customerType = searchParams.get('type') === 'privat' ? 'privat' : 'erhverv';

  const toggleCustomerType = (type) => {
    setSearchParams({ type });
  };

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/by-slug/da/priser')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente priser fra CMS');
        return res.json();
      })
      .then((data) => {
        if (data && data.collectionName === 'pages') {
          setCmsPage(data);
        }
      })
      .catch((err) => console.error(err));
  }, []);

  // Brug metadata fra CMS eller fallback
  const title = cmsPage?.meta_title || 'Priser - FLOW Transport & Logistik ApS';
  const description =
    cmsPage?.meta_description ||
    'Se vores gennemskuelige priser på transport, kurérkørsel, flytning og logistik. Få et skræddersyet tilbud tilpasset dine behov.';

  useMetadata(title, description);

  return (
    <div className="w-full bg-slate-50 font-sans">
      {/* Side Header */}
      <section className="bg-brand-dark text-white py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#0056b3_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-serif font-bold tracking-tight">
            Priser & takster
          </h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto font-sans">
            Gennemsigtige priser og faste takster. Vi tror på fuld gennemsigtighed uden skjulte overraskelser.
          </p>
        </div>
      </section>

      {/* Introduktion fra CMS */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="text-slate-600 text-lg leading-relaxed"
            dangerouslySetInnerHTML={{ __html: cmsPage?.content || '<p>Hos FLOW Transport & Logistik ApS tilbyder vi altid klare og gennemskuelige priser. Herunder finder du vores gældende takster for kurérkørsel, flytning, tillæg og øvrige ydelser.</p>' }}
          />

          {/* Kundetype Toggle */}
          <div className="flex justify-center mt-8">
            <div className="inline-flex p-1 bg-slate-100 rounded-xl border border-slate-200/60 shadow-inner">
              <button
                onClick={() => toggleCustomerType('erhverv')}
                className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 cursor-pointer flex items-center gap-2 ${
                  customerType === 'erhverv'
                    ? 'bg-white text-brand-primary shadow-sm'
                    : 'text-slate-600 hover:text-brand-primary'
                }`}
              >
                <i className="fa-solid fa-briefcase text-xs" />
                Erhverv <span className="text-xs font-normal opacity-80">(Ekskl. moms)</span>
              </button>
              <button
                onClick={() => toggleCustomerType('privat')}
                className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 cursor-pointer flex items-center gap-2 ${
                  customerType === 'privat'
                    ? 'bg-white text-brand-primary shadow-sm'
                    : 'text-slate-600 hover:text-brand-primary'
                }`}
              >
                <i className="fa-solid fa-user text-xs" />
                Privat <span className="text-xs font-normal opacity-80">(Inkl. moms)</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Prisliste Sektioner */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            
            {/* Standard kurérkørsel */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between">
              <div>
                <div className="inline-flex p-3 rounded-lg bg-brand-primary/10 text-brand-primary mb-6">
                  <Truck className="h-6 w-6" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-brand-primary mb-2">Standard kurérkørsel</h3>
                <p className="text-slate-600 text-sm mb-6">
                  Ideel til hurtig og sikker levering af mindre forsendelser.
                </p>
                <ul className="space-y-3 text-slate-700 text-sm border-t border-slate-100 pt-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary shrink-0" />
                    <span>Op til 3 genstande som kan bæres af én person</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary shrink-0" />
                    <span>Minimumspris: <strong className="text-brand-dark">{customerType === 'privat' ? '375 kr. inkl. moms' : '300 kr.'}</strong> pr. påbegyndt time</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary shrink-0" />
                    <span>Km-takst: <strong className="text-brand-dark">{customerType === 'privat' ? '10 kr. inkl. moms' : '8 kr.'}</strong> pr. km</span>
                  </li>
                </ul>
              </div>
              <div className="mt-8 pt-4 border-t border-slate-100 text-xs text-slate-500">
                {customerType === 'privat' ? '* Alle priser er inkl. moms.' : '* Alle priser er ekskl. moms medmindre andet er angivet.'}
              </div>
            </div>

            {/* Palle- og liftkørsel */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between">
              <div>
                <div className="inline-flex p-3 rounded-lg bg-brand-primary/10 text-brand-primary mb-6">
                  <Layers className="h-6 w-6" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-brand-primary mb-2">Palle- og liftkørsel</h3>
                <p className="text-slate-600 text-sm mb-6">
                  Til større forsendelser, der kræver lift eller pallehåndtering.
                </p>
                <ul className="space-y-3 text-slate-700 text-sm border-t border-slate-100 pt-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary shrink-0" />
                    <span>Ved brug af lift eller palletransport</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary shrink-0" />
                    <span>Minimumspris: <strong className="text-brand-dark">{customerType === 'privat' ? '487,50 kr. inkl. moms' : '390 kr.'}</strong> pr. påbegyndt time</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary shrink-0" />
                    <span>Km-takst: <strong className="text-brand-dark">{customerType === 'privat' ? '11,25 kr. inkl. moms' : '9 kr.'}</strong> pr. km</span>
                  </li>
                </ul>
              </div>
              <div className="mt-8 pt-4 border-t border-slate-100 text-xs text-slate-500">
                {customerType === 'privat' ? '* Alle priser er inkl. moms.' : '* Alle priser er ekskl. moms medmindre andet er angivet.'}
              </div>
            </div>

            {/* Flytning */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between">
              <div>
                <div className="inline-flex p-3 rounded-lg bg-brand-primary/10 text-brand-primary mb-6">
                  <UserCheck className="h-6 w-6" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-brand-primary mb-2">Flytning inkl. bil og 2 mand</h3>
                <p className="text-slate-600 text-sm mb-6">
                  Komplet flytteløsning med professionelle flyttefolk og rummelig flyttebil.
                </p>
                <ul className="space-y-3 text-slate-700 text-sm border-t border-slate-100 pt-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary shrink-0" />
                    <span>Minimumspris: <strong className="text-brand-dark">{customerType === 'privat' ? '1250 kr. inkl. moms' : '1000 kr. ekskl. moms'}</strong> pr. påbegyndt time</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary shrink-0" />
                    <span>Km-tillæg: <strong className="text-brand-dark">{customerType === 'privat' ? '12,50 kr. inkl. moms' : '10 kr.'}</strong> pr. km</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary shrink-0" />
                    <span>Maksimalt rumfang: <strong className="text-brand-dark">Op til 15 m³</strong></span>
                  </li>
                </ul>
              </div>
              <div className="mt-8 pt-4 border-t border-slate-100 text-xs text-slate-500">
                * Sikker og effektiv håndtering af dine ejendele.
              </div>
            </div>

            {/* Tillæg & Ekstra services */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 flex flex-col justify-between">
              <div>
                <div className="inline-flex p-3 rounded-lg bg-brand-primary/10 text-brand-primary mb-6">
                  <ShieldAlert className="h-6 w-6" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-brand-primary mb-2">Tillæg & services</h3>
                <p className="text-slate-600 text-sm mb-6">
                  Ekstra ydelser og tillæg for specielle håndteringer.
                </p>
                <ul className="space-y-3 text-slate-700 text-sm border-t border-slate-100 pt-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-brand-dark">Byrdetillæg:</strong> Genstande over 95 kg afregnes med <strong className="text-brand-dark">{customerType === 'privat' ? '250 kr. inkl. moms' : '200 kr.'}</strong> pr. enhed pr. mand.
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-brand-dark">Opbæring:</strong> Hjælp til opbæring afregnes med <strong className="text-brand-dark">{customerType === 'privat' ? '75 kr. inkl. moms' : '60 kr.'}</strong> pr. genstand.
                    </div>
                  </li>
                  {customerType === 'erhverv' && (
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-brand-primary shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-brand-dark">Udlån af chauffør:</strong> <strong className="text-brand-dark">500 kr.</strong> pr. påbegyndt time.
                        <div className="text-xs text-slate-500 mt-0.5">Udlån over længere periode - ring for aftale</div>
                      </div>
                    </li>
                  )}
                </ul>
              </div>
              <div className="mt-8 pt-4 border-t border-slate-100 text-xs text-slate-500">
                * Sikrer korrekt mandskab og udstyr til tunge løft.
              </div>
            </div>

          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Aften-, nat- og weekendtillæg */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 lg:col-span-2">
              <div className="inline-flex p-3 rounded-lg bg-brand-primary/10 text-brand-primary mb-6">
                <Clock className="h-6 w-6" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-brand-primary mb-4">Aften-, nat- og weekendtillæg</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div className="space-y-3 bg-slate-50 p-5 rounded-xl border border-slate-100">
                  <h4 className="font-bold text-brand-dark border-b border-slate-200 pb-2">Mandag - fredag:</h4>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex justify-between">
                      <span>Kl. 18:00 - 23:59</span>
                      <span className="font-semibold text-brand-primary">+{customerType === 'privat' ? '125 kr. inkl. moms' : '100 kr.'} pr. påbegyndt time</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Kl. 00:00 - 06:00</span>
                      <span className="font-semibold text-brand-primary">+{customerType === 'privat' ? '250 kr. inkl. moms' : '200 kr.'} pr. påbegyndt time</span>
                    </li>
                  </ul>
                </div>

                <div className="space-y-3 bg-slate-50 p-5 rounded-xl border border-slate-100">
                  <h4 className="font-bold text-brand-dark border-b border-slate-200 pb-2">Lørdag, søndag & helligdage:</h4>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex justify-between gap-4">
                      <span>Kl. 08:00 - 15:59 <span className="text-xs text-slate-500">(kun søn & helligdage)</span></span>
                      <span className="font-semibold text-brand-primary">+{customerType === 'privat' ? '100 kr. inkl. moms' : '75 kr.'} pr. påbegyndt time</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Kl. 16:00 - 23:59</span>
                      <span className="font-semibold text-brand-primary">+{customerType === 'privat' ? '187,50 kr. inkl. moms' : '150 kr.'} pr. påbegyndt time</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Kl. 00:00 - 06:00</span>
                      <span className="font-semibold text-brand-primary">+{customerType === 'privat' ? '312,50 kr. inkl. moms' : '250 kr.'} pr. påbegyndt time</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Øvrige gebyrer */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
              <div className="inline-flex p-3 rounded-lg bg-brand-primary/10 text-brand-primary mb-6">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-brand-primary mb-4">Øvrige gebyrer</h3>
              
              <ul className="space-y-3 text-slate-700 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-brand-primary font-bold">•</span>
                  <span><strong>Forgæves ankomst:</strong> Beregnes som minimumspris pr. tur.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand-primary font-bold">•</span>
                  <span><strong>Natopbevaring:</strong> Opbevaring af gods på bilen + udkørsel næste dag: <strong>{customerType === 'privat' ? '437,50 kr. inkl. moms' : '350 kr.'}</strong> + ny kørsel fra startadresse.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand-primary font-bold">•</span>
                  <span><strong>Faktureringsgebyr:</strong> <strong>{customerType === 'privat' ? '43,75 kr. inkl. moms' : '35 kr.'}</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand-primary font-bold">•</span>
                  <span>Brændstof- og miljøtillæg kan forekomme.</span>
                </li>
              </ul>
            </div>

          </div>

        </div>
      </section>

      {/* Skræddersyede Løsninger CTA */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Venstre: Tekst */}
            <div className="space-y-6">
              <div className="text-sm font-bold text-brand-primary uppercase tracking-wider">
                Skræddersyede aftaler
              </div>
              <h2 className="text-3xl font-serif font-bold text-brand-primary tracking-tight">
                Løsninger tilpasset din virksomhed
              </h2>
              <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                Vi tilbyder attraktive rammeaftaler og mængderabatter til virksomheder med regelmæssige transport- og logistikbehov. Med en fast aftale får du en dedikeret kontaktperson, garanteret kapacitet og faste, gennemskuelige priser.
              </p>
              
              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center bg-brand-primary hover:bg-brand-primary/95 text-white px-6 py-3 rounded-lg text-sm font-bold uppercase tracking-wider transition-colors shadow cursor-pointer"
                >
                  Indhent et uforpligtende tilbud
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>

            {/* Højre: Boks med info */}
            <div className="bg-slate-50 border border-slate-100 p-8 sm:p-10 rounded-2xl space-y-6">
              <h3 className="text-2xl font-serif font-bold text-brand-primary">
                Få et hurtigt prisoverslag
              </h3>
              <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                For at give dig den mest præcise pris, har vi typisk brug for følgende oplysninger, når du kontakter os:
              </p>
              <ul className="space-y-3 text-xs sm:text-sm text-slate-700">
                <li className="flex items-start gap-2.5">
                  <span className="bg-brand-primary text-white h-5 w-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</span>
                  <span>Afhentnings- og leveringsadresse (postnumre)</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="bg-brand-primary text-white h-5 w-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</span>
                  <span>Godstype, dimensioner (L x B x H) og vægt</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="bg-brand-primary text-white h-5 w-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">3</span>
                  <span>Ønsket afhentnings- og leveringstidspunkt</span>
                </li>
              </ul>
              <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row gap-4 justify-between items-center text-xs text-slate-500">
                <div className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4 text-brand-primary" />
                  <span className="whitespace-nowrap font-semibold">Ring: 22 28 93 10</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Mail className="h-4 w-4 text-brand-primary" />
                  <span className="whitespace-nowrap font-semibold">Mail: kontakt@flowtl.dk</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
